// src/AudioSpectrum/effects/dancingFlowers.js
import { getHSLColor } from '../colorUtils.js';

export const drawDancingFlowers = (ctx, frequencyData, width, height, flowersRef) => {
  // 배경 - 어두운 밤하늘 같은 느낌
  ctx.fillStyle = 'rgba(5, 10, 15, 0.1)';
  ctx.fillRect(0, 0, width, height);
  
  // 땅 부분
  const groundY = height * 0.8; 
  
  // 초기화: 꽃들이 없으면 생성
  if (!flowersRef.current.flowers) {
    flowersRef.current.flowers = [];
    flowersRef.current.heatWaves = [];
    flowersRef.current.petalParticles = [];
  }
  
  if (flowersRef.current.flowers.length === 0) {
    const flowerCount = 350; // 꽃 수 증가
    for (let i = 0; i < flowerCount; i++) {
      flowersRef.current.flowers.push({
        x: (width / flowerCount) * i + Math.random() * (width / flowerCount) * 0.8,
        y: groundY - Math.random() * 30,
        baseY: groundY - Math.random() * 30,
        stemHeight: Math.random() * 25 + 15, // 줄기 크기 감소
        petalColor: Math.random() < 0.4 ? 'white' : (Math.random() < 0.5 ? 'yellow' : 'pink'),
        swayAmount: 0,
        swaySpeed: Math.random() * 0.005 + 0.002, // 기본 움직임 속도 감소
        size: Math.random() * 5 + 3, // 꽃 크기 약간 감소
        phase: Math.random() * Math.PI * 2,
        freqBand: Math.floor(Math.random() * Math.min(frequencyData.length, 150)),
        isActive: false, // 음악에 반응하는 상태
        activationThreshold: Math.random() * 50 + 30 // 각 꽃마다 다른 활성화 임계값
      });
    }
  }
  
  // 주파수 데이터 분석
  const avgFreq = frequencyData.reduce((a, b) => a + b, 0) / frequencyData.length;
  const bassAvg = frequencyData.slice(0, 20).reduce((a, b) => a + b, 0) / 20;
  const midAvg = frequencyData.slice(20, 100).reduce((a, b) => a + b, 0) / 80;
  const highAvg = frequencyData.slice(100, 200).reduce((a, b) => a + b, 0) / 100;
  
  // 음악 강도에 따른 아지랑이 생성
  if (avgFreq > 25) {
    const heatIntensity = Math.min(avgFreq / 255, 1);
    const heatWaveCount = Math.floor(heatIntensity * 2) + 1;
    
    for (let i = 0; i < heatWaveCount; i++) {
      flowersRef.current.heatWaves.push({
        type: 'heatWave',
        x: Math.random() * width,
        y: groundY + Math.random() * 15 - 20,
        vy: -Math.random() * 1.5 - 0.5,
        life: 1.0,
        decay: 0.006,
        size: Math.random() * 25 + 25,
        opacity: Math.random() * 0.4 + 0.1
      });
    }
  }

  // 음악 강도에 따른 꽃잎 파티클 생성
  if (avgFreq > 40 && Math.random() < 0.3) {
    const petalIntensity = Math.min(avgFreq / 255, 1);
    const petalCount = Math.floor(petalIntensity * 8) + 3;
    
    for (let i = 0; i < petalCount; i++) {
      const colors = ['white', 'yellow', 'pink'];
      const petalColor = colors[Math.floor(Math.random() * colors.length)];
      
      flowersRef.current.petalParticles.push({
        x: Math.random() * width,
        y: groundY - Math.random() * 100,
        vx: (Math.random() - 0.5) * 4,
        vy: -Math.random() * 2 - 1,
        life: 1.0,
        decay: 0.003,
        size: Math.random() * 4 + 2,
        color: petalColor,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.2,
        gravity: 0.05,
        wind: Math.sin(Date.now() * 0.001) * 0.5
      });
    }
  }
  
  // 아지랑이 그리기 및 업데이트
  flowersRef.current.heatWaves = flowersRef.current.heatWaves.filter(item => {
    item.y += item.vy;
    item.life -= item.decay;
    item.size += 0.3;
    
    if (item.life > 0 && item.y > -50) {
      ctx.globalAlpha = item.life * item.opacity;
      
      // 아지랑이 효과 - 부드러운 물결 모양
      const waveHeight = 8;
      const waveLength = item.size;
      
      ctx.strokeStyle = `rgba(255, 255, 255, ${item.life * 0.2})`;
      ctx.lineWidth = 1;
      ctx.beginPath();
      
      for (let x = -waveLength; x <= waveLength; x += 3) {
        const waveY = item.y + Math.sin((x / waveLength) * Math.PI * 3 + Date.now() * 0.005) * waveHeight * item.life;
        if (x === -waveLength) {
          ctx.moveTo(item.x + x, waveY);
        } else {
          ctx.lineTo(item.x + x, waveY);
        }
      }
      ctx.stroke();
      
      ctx.globalAlpha = 1.0;
      return true;
    }
    return false;
  });

  // 꽃잎 파티클 그리기 및 업데이트
  if (!flowersRef.current.petalParticles) {
    flowersRef.current.petalParticles = [];
  }
  
  flowersRef.current.petalParticles = flowersRef.current.petalParticles.filter(petal => {
    petal.x += petal.vx + petal.wind;
    petal.y += petal.vy;
    petal.vy += petal.gravity;
    petal.vx *= 0.995; // 공기 저항
    petal.life -= petal.decay;
    petal.rotation += petal.rotationSpeed;
    
    if (petal.life > 0 && petal.y < height + 50 && petal.x > -50 && petal.x < width + 50) {
      ctx.globalAlpha = petal.life;
      ctx.save();
      ctx.translate(petal.x, petal.y);
      ctx.rotate(petal.rotation);
      
      // 꽃잎 색상 설정
      let color;
      switch(petal.color) {
        case 'white':
          color = getHSLColor(0, 0, 90);
          break;
        case 'yellow':
          color = getHSLColor(50, 80, 70);
          break;
        case 'pink':
          color = getHSLColor(320, 70, 75);
          break;
      }
      
      // 꽃잎 모양 그리기
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.scale(1, 0.6);
      ctx.arc(0, 0, petal.size, 0, Math.PI * 2);
      ctx.fill();
      
      // 꽃잎 중심 점
      ctx.fillStyle = getHSLColor(45, 70, 60);
      ctx.beginPath();
      ctx.arc(0, 0, petal.size * 0.3, 0, Math.PI * 2);
      ctx.fill();
      
      ctx.restore();
      ctx.globalAlpha = 1.0;
      return true;
    }
    return false;
  });
  
  // 꽃들 그리기 및 애니메이션
  flowersRef.current.flowers.forEach(flower => {
    // 주파수에 따른 활성화 여부 결정
    const freqValue = frequencyData[flower.freqBand] || 0;
    const intensity = freqValue / 255;
    
    // 꽃의 활성화 상태 업데이트
    if (freqValue > flower.activationThreshold) {
      flower.isActive = true;
    } else if (freqValue < flower.activationThreshold * 0.7) {
      flower.isActive = false;
    }
    
    // 다양한 주파수 대역의 영향 (더 부드럽게)
    const bassInfluence = bassAvg / 255 * 0.1;
    const midInfluence = midAvg / 255 * 0.2;
    const highInfluence = highAvg / 255 * 0.1;
    
    // 활성화된 꽃만 음악에 반응
    let totalInfluence = 0;
    if (flower.isActive) {
      totalInfluence = (intensity + bassInfluence + midInfluence + highInfluence) * 0.8; // 움직임 강도 감소
    }
    
    // 자연스러운 흔들림 (기본 바람 효과)
    flower.phase += flower.swaySpeed;
    const naturalSway = Math.sin(flower.phase) * 2; // 기본 움직임 감소
    
    // 음악에 반응하는 움직임 (부드럽게)
    const musicSway = flower.isActive ? 
      Math.sin(Date.now() * 0.008 + flower.x * 0.005) * totalInfluence * 8 : // 음악 반응 감소
      0;
    
    flower.swayAmount = naturalSway + musicSway;
    
    // 줄기 그리기 (더 얇게)
    ctx.strokeStyle = getHSLColor(100, 50, 25);
    ctx.lineWidth = 1.5; // 줄기 두께 감소
    ctx.beginPath();
    
    const segments = 6; // 세그먼트 수 감소
    const segmentHeight = flower.stemHeight / segments;
    
    for (let i = 0; i <= segments; i++) {
      const segmentY = flower.baseY - (i * segmentHeight);
      const swayAtSegment = flower.swayAmount * (i / segments) * (i / segments) * 0.5; // 움직임 감소
      
      const x = flower.x + swayAtSegment;
      
      if (i === 0) {
        ctx.moveTo(x, segmentY);
      } else {
        ctx.lineTo(x, segmentY);
      }
    }
    ctx.stroke();
    
    // 꽃 중심 위치
    const flowerX = flower.x + flower.swayAmount * 0.5; // 꽃머리 움직임 더 감소
    const flowerY = flower.baseY - flower.stemHeight;
    
    // 꽃잎 색상 설정
    let petalColor;
    const colorIntensity = flower.isActive ? intensity * 15 : 0;
    
    switch(flower.petalColor) {
      case 'white':
        petalColor = getHSLColor(0, 0, 85 + colorIntensity);
        break;
      case 'yellow':
        petalColor = getHSLColor(50, 70 + colorIntensity, 65 + colorIntensity * 0.5);
        break;
      case 'pink':
        petalColor = getHSLColor(320, 60 + colorIntensity, 70 + colorIntensity * 0.5);
        break;
    }
    
    // 꽃잎 그리기 (5개 꽃잎)
    const petalCount = 5;
    const petalSize = flower.size + (flower.isActive ? intensity * 1.5 : 0); // 크기 변화 감소
    
    for (let p = 0; p < petalCount; p++) {
      const angle = (p / petalCount) * Math.PI * 2 + flower.phase * 0.2; // 회전 속도 감소
      const petalDistance = petalSize * 0.7;
      
      const petalX = flowerX + Math.cos(angle) * petalDistance;
      const petalY = flowerY + Math.sin(angle) * petalDistance;
      
      // 꽃잎 모양 (타원)
      ctx.fillStyle = petalColor;
      ctx.beginPath();
      ctx.save();
      ctx.translate(petalX, petalY);
      ctx.rotate(angle);
      ctx.scale(1, 0.6);
      ctx.arc(0, 0, petalSize * 0.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
    
    // 꽃 중심 (노란색)
    const centerBrightness = flower.isActive ? 50 + intensity * 15 : 45;
    ctx.fillStyle = getHSLColor(45, 80, centerBrightness);
    ctx.beginPath();
    ctx.arc(flowerX, flowerY, flower.size * 0.25 + (flower.isActive ? intensity * 1 : 0), 0, Math.PI * 2);
    ctx.fill();
    
    // 강한 음악일 때만 반짝이는 효과
    if (flower.isActive && intensity > 0.8) {
      ctx.fillStyle = `rgba(255, 255, 255, ${intensity * 0.3})`;
      ctx.beginPath();
      ctx.arc(flowerX, flowerY, flower.size * 0.4, 0, Math.PI * 2);
      ctx.fill();
      
      // 스파크 효과 (더 부드럽게)
      const sparkCount = Math.floor(intensity * 3);
      for (let s = 0; s < sparkCount; s++) {
        const sparkAngle = Math.random() * Math.PI * 2;
        const sparkDistance = Math.random() * 12 + 6;
        const sparkX = flowerX + Math.cos(sparkAngle) * sparkDistance;
        const sparkY = flowerY + Math.sin(sparkAngle) * sparkDistance;
        
        ctx.fillStyle = `rgba(255, 255, 255, ${Math.random() * 0.5})`;
        ctx.beginPath();
        ctx.arc(sparkX, sparkY, Math.random() * 1.5 + 0.5, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  });
};